import { Linear, VideoClicks, TrackingEvents } from '@lib/iab/iab-vast-model';

import createClick from './click';
import createIcon from './icon';
import createInteractiveMediaFile from './interactive-creative-file';
import createMediaFile from './media-file';
import createTimeOffset from './time-offset';
import hasValue from '../util/has-value';
import isNonEmptyArray from '../util/is-non-empty-array';
import isNonEmptyString from '../util/is-non-empty-string';
import mapTrackingEvents from '../util/map-tracking-events';

interface UnmarshalerNode {
    _value: unknown;
    [key: string]: unknown;
}

interface ParseOptions {
    errorHandler: {
        tryRecover: (error: Error) => void;
    };
}

const mapVideoClicks = (
    $videoClicks: UnmarshalerNode,
    videoClicks: VideoClicks
): void => {
    if (
        $videoClicks.clickThrough != null &&
        hasValue($videoClicks.clickThrough)
    ) {
        videoClicks.clickThrough = createClick(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            $videoClicks.clickThrough as any
        );
    }
    if ($videoClicks.clickTracking != null) {
        videoClicks.clickTrackings.push(
            ...($videoClicks.clickTracking as UnmarshalerNode[])
                .filter(hasValue)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((click: UnmarshalerNode) => createClick(click as any))
        );
    }
    if ($videoClicks.customClick != null) {
        videoClicks.customClicks.push(
            ...($videoClicks.customClick as UnmarshalerNode[])
                .filter(hasValue)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .map((click: UnmarshalerNode) => createClick(click as any))
        );
    }
};

const createLinear = (
    $creative: UnmarshalerNode,
    options: ParseOptions
): Linear => {
    const $linear = $creative.linear as UnmarshalerNode;
    const linear = new Linear();

    if (isNonEmptyString($linear.skipoffset)) {
        linear.skipoffset = createTimeOffset(
            $linear.skipoffset as string,
            options,
            new Error()
        );
    }

    if ($linear.duration != null) {
        linear.duration = ($linear.duration as UnmarshalerNode)
            ._value as number;
    }

    if ($linear.mediaFiles != null) {
        const $mediaFiles = $linear.mediaFiles as UnmarshalerNode;
        if ($mediaFiles.mezzanine != null) {
            linear.mezzanine = ($mediaFiles.mezzanine as UnmarshalerNode)
                ._value as string;
        }
        if ($mediaFiles.mediaFile != null) {
            linear.mediaFiles.push(
                ...($mediaFiles.mediaFile as UnmarshalerNode[]).map(
                    (mediaFile: UnmarshalerNode) =>
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        createMediaFile(mediaFile as any)
                )
            );
        }
        if ($mediaFiles.interactiveCreativeFile != null) {
            linear.interactiveCreativeFiles.push(
                ...(
                    $mediaFiles.interactiveCreativeFile as UnmarshalerNode[]
                ).map((interactiveFile: UnmarshalerNode) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    createInteractiveMediaFile(interactiveFile as any)
                )
            );
        }
    }

    if ($linear.adParameters != null) {
        linear.adParameters = ($linear.adParameters as UnmarshalerNode)
            ._value as string;
    }

    if ($linear.videoClicks != null) {
        linear.videoClicks = new VideoClicks();
        mapVideoClicks(
            $linear.videoClicks as UnmarshalerNode,
            linear.videoClicks
        );
    }

    if (
        $linear.trackingEvents != null &&
        isNonEmptyArray(($linear.trackingEvents as UnmarshalerNode).tracking)
    ) {
        linear.trackingEvents = new TrackingEvents();
        mapTrackingEvents(
            $linear.trackingEvents as UnmarshalerNode,
            linear.trackingEvents,
            options
        );
    }

    if ($linear.icons != null && ($linear.icons as UnmarshalerNode).icon) {
        linear.icons.push(
            ...(
                ($linear.icons as UnmarshalerNode).icon as UnmarshalerNode[]
            ).map((icon: UnmarshalerNode) =>
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                createIcon(icon as any)
            )
        );
    }

    return linear;
};

export default createLinear;
